import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

// ==========================================
// TYPE DEFINITIONS — mirror the SDK DTOs
// ==========================================

export type LearnerReportType = 'COURSE' | 'LEARNING_PATH' | 'CERTIFICATION' | 'ALL';
export type UrgencyLevel = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type TimelineStatus = 'COMPLETED' | 'OVERDUE' | 'DUE_SOON' | 'ON_TRACK' | 'NO_DEADLINE';
export type LearnerTrackingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface LearnerActivityReportRequest {
    report_type: LearnerReportType;
    timeline_status?: TimelineStatus;
    tracking_status?: LearnerTrackingStatus;
    urgency_levels?: UrgencyLevel[];
    department_ids?: string[];
    course_ids?: string[];
    learning_path_ids?: string[];
    certification_ids?: string[];
    mandatory_only?: boolean;
    search?: string;
    page?: number;
    limit?: number;
    _export_format?: string;
}

export interface LearnerRecord {
    user_id: string;
    user_name: string;
    user_email: string;
    employee_id?: string;
    department: string;
    role: string;
    reporting_manager_name: string;
    reporting_manager_email: string;
    hrbp_name: string;
    hrbp_email: string;
    bu_head_name: string;
    bu_head_email: string;

    item_type: LearnerReportType;
    item_id: string;
    item_title: string;
    assignment_id: string;

    is_mandatory: boolean;
    tracking_status: LearnerTrackingStatus;
    timeline_status: TimelineStatus;

    progress_percentage: number;
    deadline_date: string | null;
    days_left: number | null;
    days_overdue: number;
    urgency_level: UrgencyLevel;

    started_at: string | null;
    last_accessed_at: string | null;
    completed_at: string | null;

    total_time_spent_minutes: number;
    reminder_count: number;
    assigned_date: string;

    completed_modules?: number;
    total_modules?: number;
    attempts_used?: number;
    max_attempts?: number | null;
    category_name?: string;
}

export interface LearnerActivitySummary {
    total_assignments: number;
    unique_users: number;
    unique_items: number;

    completed_count: number;
    overdue_count: number;
    due_soon_count: number;
    on_track_count: number;
    no_deadline_count: number;

    not_started_count: number;
    in_progress_count: number;

    mandatory_total: number;
    mandatory_overdue: number;

    completion_rate: number;
    avg_progress: number;
    avg_time_spent_minutes: number;

    critical_count: number;
    high_count: number;
    medium_count: number;
    low_count: number;
}

export interface LearnerByDepartment {
    department: string;
    count: number;
}

export interface LearnerByItem {
    item_title: string;
    item_type: LearnerReportType;
    count: number;
}

export interface LearnerPagination {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
}

export interface LearnerActivityReportResponse {
    generated_at: string;
    report_type: LearnerReportType;
    summary: LearnerActivitySummary;
    records: LearnerRecord[];
    pagination: LearnerPagination;
    by_department: LearnerByDepartment[];
    by_item: LearnerByItem[];
}

// ==========================================
// SERVICE
// ==========================================

class LearnerReportService {
    /** Summary only — drives cards + charts (skips table filters server-side) */
    async getSummary(report_type: LearnerReportType): Promise<LearnerActivityReportResponse> {
        const response = await apiClient.post(API_ENDPOINTS.LEARNER_ACTIVITY_REPORT.SUMMARY, { report_type });
        return response.data?.data || response.data;
    }

    /** Paginated list with all filters applied */
    async generateReport(params: LearnerActivityReportRequest): Promise<LearnerActivityReportResponse> {
        const response = await apiClient.post(API_ENDPOINTS.LEARNER_ACTIVITY_REPORT.GENERATE, params);
        return response.data?.data || response.data;
    }

    /** All matching records (no pagination) for Excel export */
    async exportRecords(params: Omit<LearnerActivityReportRequest, 'page' | 'limit'>): Promise<LearnerActivityReportResponse> {
        const response = await apiClient.post(API_ENDPOINTS.LEARNER_ACTIVITY_REPORT.EXPORT, params);
        return response.data?.data || response.data;
    }
}

export const learnerReportService = new LearnerReportService();
